import ProfileIcon from '../assets/icons/profile.svg';
import EnvelopeIcon from '../assets/icons/envelope.svg';
import VerifyIcon from '../assets/icons/verify.svg';
import SettingIcon from '../assets/icons/settings (5) 1.svg';

const BASE_STEPS = [
  {
    id: 1,
    title: 'Create Your Secure Account',
    description: 'Enter your name, email, and password to get started.',
    icon: ProfileIcon,
    alt: 'Create Account',
  },
  {
    id: 2,
    title: 'Verify Your Email',
    description: 'Confirm your email to activate your access.',
    icon: EnvelopeIcon,
    alt: 'Verify Email',
  },
  {
    id: 3,
    title: 'Register Your Blood Test',
    description: 'Register a test now, or skip and do it later.',
    icon: VerifyIcon,
    alt: 'Register Blood Test',
    iconAdjustClass: 'translate-y-[1px]',
  },
  {
    id: 4,
    title: 'Complete Your Profile',
    description: 'Add your details for accurate, personalized results.',
    icon: SettingIcon,
    alt: 'Complete Profile',
    iconAdjustClass: 'translate-y-[1px]',
  },
];

const STEPS_WITH_PASSWORD = [
  {
    id: 1,
    title: 'Create Your Secure Account',
    description: 'Enter your name, email, and password to get started.',
    icon: ProfileIcon,
    alt: 'Create Account',
  },
  {
    id: 2,
    title: 'Verify Your Email',
    description: 'Confirm your email to activate your access.',
    icon: EnvelopeIcon,
    alt: 'Verify Email',
  },
  {
    id: 3,
    title: 'Set a New Password',
    description: 'Create a new password to secure your account.',
    icon: SettingIcon,
    alt: 'Set New Password',
    iconAdjustClass: 'translate-y-[1px]',
  },
  {
    id: 4,
    title: 'Register Your Blood Test',
    description: 'Register a test now, or skip and do it later.',
    icon: VerifyIcon,
    alt: 'Register Blood Test',
    iconAdjustClass: 'translate-y-[1px]',
  },
  {
    id: 5,
    title: 'Complete Your Profile',
    description: 'Add your details for accurate, personalized results.',
    icon: SettingIcon,
    alt: 'Complete Profile',
    iconAdjustClass: 'translate-y-[1px]',
  },
];

export default function StageTracker({ activeStep = 1, showPasswordStep = false }) {
  const STEPS = showPasswordStep ? STEPS_WITH_PASSWORD : BASE_STEPS;

  return (
    <div className="flex flex-col gap-5 sm:gap-5 md:gap-6 pb-2 md:pb-3 mt-4 md:mt-5">
      <div className="flex flex-col gap-1.5 md:gap-2">
        <h1 className="text-[22px] sm:text-[24px] md:text-[26px] lg:text-[30px] font-libreBaskerville font-normal leading-tight text-[#2B3133]">
          Get Started with Biowell
        </h1>
        <p className="text-[13px] sm:text-[14px] md:text-[15px] lg:text-[16px] font-poppins font-normal text-[#2B3133]">
          Simple steps to activate your test and access your results.
        </p>
      </div>

      {/* Steps List */}
      <div className="flex flex-col gap-12 relative mt-1 md:mt-2">
        {STEPS.map((step) => {
          const isActive = step.id === activeStep;
          const isCompleted = step.id < activeStep;
          const isInactive = step.id > activeStep;
          const hasNextStep = step.id < STEPS.length;
          const connectorFillPercent =
            step.id < activeStep ? 100 : step.id === activeStep ? 50 : 0;
          const titleColor = isInactive ? 'text-[#889696]' : 'text-[#2B3133]';
          const descColor = isInactive ? 'text-[#889696]' : 'text-[#2B3133]';

          return (
            <div
              key={step.id}
              className="flex items-start gap-4 sm:gap-5 md:gap-6 relative z-10"
            >
              {hasNextStep && (
                <div
                  className="absolute left-[23px] md:left-[25px] top-[23px] md:top-[25px] -translate-x-1/2 w-[2px] bg-[#E5E5E5]"
                  style={{ height: 'calc(100% + 48px)' }}
                >
                  {connectorFillPercent > 0 && (
                    <div
                      className="w-full bg-[#119975] transition-all duration-300"
                      style={{ height: `${connectorFillPercent}%` }}
                    />
                  )}
                </div>
              )}
              <div
                className={`flex-shrink-0 w-[46px] h-[46px] sm:w-[46px] sm:h-[46px] md:w-[50px] md:h-[50px] relative flex items-center justify-center overflow-hidden rounded-full ${
                  isCompleted
                    ? 'bg-[#119975]'
                    : isActive
                    ? 'border-2 border-[#119975] bg-white'
                    : 'bg-[#E5E5E5]'
                }`}
              >
                {isCompleted ? (
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="white"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="flex-shrink-0"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                ) : (
                  <>
                    {isActive ? (
                      <span
                        aria-label={step.alt}
                        className={`w-5 h-5 md:w-6 md:h-6 block ${step.iconAdjustClass || ''}`}
                        style={{
                          backgroundColor: '#119975',
                          WebkitMaskImage: `url("${encodeURI(step.icon)}")`,
                          maskImage: `url("${encodeURI(step.icon)}")`,
                          WebkitMaskRepeat: 'no-repeat',
                          maskRepeat: 'no-repeat',
                          WebkitMaskPosition: 'center',
                          maskPosition: 'center',
                          WebkitMaskSize: 'contain',
                          maskSize: 'contain',
                        }}
                      />
                    ) : (
                      <img
                        src={step.icon}
                        alt={step.alt}
                        className={`w-5 h-5 md:w-6 md:h-6 object-contain ${step.iconAdjustClass || ''} ${
                          isInactive ? 'opacity-60' : ''
                        }`}
                      />
                    )}
                  </>
                )}
              </div>
              <div className="flex flex-col gap-1.5 min-w-0 flex-1 pt-0.5">
                <h3
                  className={`text-[15px] md:text-[16px] font-poppins font-semibold leading-tight ${titleColor}`}
                >
                  {step.title}
                </h3>
                <p
                  className={`text-[14px] md:text-[15px] font-poppins font-normal ${descColor}`}
                >
                  {step.description}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
